import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { css } from '@eti/styles';
import isFunction from 'lodash/isFunction';
import { m as motion, AnimatePresence } from 'framer-motion';
import { analytics, usePrevious } from '@eti/utils';
import { useTranslation, useProperty } from '@eti/providers';
import { useDidUpdateEffect } from '../../../custom-hooks/useDidUpdateEffect';
import { getAvailableDeckOptions } from '../../utils/decks';
import { findIndexOfSegment, findNextAvailableSegment, findPreviousAvailableSegment, isFirstAvailableSegment, isLastAvailableSegment, getNonSelectedAvailableSegments } from '../../utils/segments';
import { getTotalSelectedSeatsForTravelers } from '../../utils/currentSeatSelection';
import { getIdOfFirstUnselectedTraveler, getNextSegment, getSegmentTravelers, getNextTravelerId, getPreviousTravelerId, getNonBookedTravelerSeats } from '../../utils/travelers';
import { SEATING_PREFERENCE_OPTIONS, CONFIRMATION_MODAL_REDIRECT_TO, CONFIRMATION_MODAL_ACTION_TYPE, CONFIRMATION_MODAL_TYPE } from '../../utils/constants';
import Summary from '../../summary/components/SummaryTable';
import { SEAT_MAP } from '../../../products';
import { flatBounds } from '../../utils/bounds';
import CloseIcon from './SeatMapCloseIcon';
import SeatMapHeader from './SeatMapHeader';
import MainContent from './SeatMapMainContent';
import Footer from './SeatMapFooter';
import SeatMapConfirmation from './SeatMapConfirmation';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const seatMapWrapperStyles = backDrop => css`
  background-color: #fff;
  border-radius: 0.1875rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  ${backDrop ? `&::before {
      background-color: rgb(0 0 0 / 70%);
      border-radius: 0.1875rem;
      content: '';
      inset: 0;
      overflow: hidden;
      position: absolute;
      z-index: 4;
    }` : ''};
  overflow: hidden;
  position: relative;
`;
const footerWrapperStyles = showAllSeatsSelectedConfirmation => css`
  display: ${showAllSeatsSelectedConfirmation ? 'none' : 'block'};
`;
const motionVariants = {
  enter: {
    opacity: 1
  },
  exit: {
    opacity: 0
  }
};
const SeatMapContent = _ref => {
  let {
    bounds = [],
    callback = () => {},
    getSelected = () => [],
    isPostBooking,
    isShowingCloseIcon,
    moveToExtraProductsPage,
    onChange = () => {},
    onClose,
    onOptOut,
    onRemove = () => {},
    productSpecificTexts,
    selectedSeats,
    shouldShowConfirmationModal,
    totalMarkup,
    totalPrice,
    travelers
  } = _ref;
  const {
    p
  } = useProperty();
  const {
    t
  } = useTranslation();
  const usePersuasionConfiguration = p('IbeClient.TravelerDetails.OptionalProductsRequireValidation.Enabled');
  const lowerDeckLabel = t('Product.SeatMap.DeckSelection.LowerDeck');
  const mainDeckLabel = t('Product.SeatMap.DeckSelection.MainDeck');
  const upperDeckLabel = t('Product.SeatMap.DeckSelection.UpperDeck');
  const flattenedBounds = useMemo(() => flatBounds(bounds), [bounds]);
  const firstAvailableSegment = getNextSegment(flattenedBounds);
  const getActiveDeck = options => {
    const mainDeck = options.find(deck => deck.value === 'mainDeck');
    return mainDeck || options[0];
  };
  const getAvailableDeckOptionsForSegment = index => {
    const currentSegment = flattenedBounds.find(_ref2 => {
      let {
        boundSegmentIndex
      } = _ref2;
      return boundSegmentIndex === index;
    });
    return getAvailableDeckOptions(currentSegment, upperDeckLabel, mainDeckLabel, lowerDeckLabel);
  };
  const firstAvailableSegmentActiveDeck = getActiveDeck(getAvailableDeckOptionsForSegment(firstAvailableSegment.boundSegmentIndex));
  const getInitialActiveTravelerId = travelerIds => (travelerIds || [])[0] || 0; // TODO: Is this really correct? Will always resolve to first traveler

  const [activeDeck, setActiveDeck] = useState(firstAvailableSegmentActiveDeck);
  const [activeSegment, setActiveSegment] = useState(firstAvailableSegment.boundSegmentIndex);
  const [activeTraveler, setActiveTraveler] = useState(getInitialActiveTravelerId(firstAvailableSegment.travelerIds));
  const [isSummaryShowing, setIsSummaryShowing] = useState(false);
  const [fromSummary, setFromSummary] = useState(false);
  const [direction, setDirection] = useState(0);
  const [activeBoundIndex, activeSegmentIndex] = activeSegment.split('-').map(Number);
  const previousActiveSegment = usePrevious(activeSegment);
  const [animate, setAnimate] = useState();
  const [backDrop, setBackDrop] = useState(false);
  const [showAllSeatsSelectedConfirmation, setShowAllSeatsSelectedConfirmation] = useState();
  const [confirmationModalRedirectTo, setConfirmationModalRedirectTo] = useState(CONFIRMATION_MODAL_REDIRECT_TO.DEFAULT);
  const [confirmationModalType, setConfirmationModalType] = useState(CONFIRMATION_MODAL_TYPE.SEAT_MAP_PERSIST_IN_PRODUCT_UNBOOKED_SEGMENTS);
  // TODO: remove this lagacy logic isCancelButtonDisabled property and usage of that
  const isCancelButtonDisabled = p('IbeClient.SeatMap.Footer.CancelButton.Disabled');
  const isManualNavigationEnabled = p('IbeClient.SeatMap.Segment.Navigation.Manual.Enabled');
  const totalSelectedSeats = getTotalSelectedSeatsForTravelers(selectedSeats);
  const nonSelectedAvailableSegments = getNonSelectedAvailableSegments(flattenedBounds, totalSelectedSeats);
  const allSeatsSelected = (nonSelectedAvailableSegments || []).length === 0;
  const onNavigationClick = doAnimate => {
    setAnimate(doAnimate);
  };
  const handleBackDrop = open => {
    setBackDrop(open);
  };
  useEffect(() => {
    const isDifferentSegment = previousActiveSegment && previousActiveSegment !== activeSegment;
    const getDirection = (boundIndex, previousBoundIndex, segmentIndex, previousSegmentIndex) => {
      if (boundIndex >= previousBoundIndex) {
        if (boundIndex === previousBoundIndex) {
          if (segmentIndex > previousSegmentIndex) {
            return 1;
          }
          if (segmentIndex < previousSegmentIndex) {
            return -1;
          }
          return 0;
        }
        return 1;
      }
      return -1;
    };
    if (isDifferentSegment) {
      const [previousBoundIndex, previousSegmentIndex] = previousActiveSegment.split('-').map(Number);
      const dir = getDirection(activeBoundIndex, previousBoundIndex, activeSegmentIndex, previousSegmentIndex);
      setDirection(dir);
    }
  }, [activeBoundIndex, activeSegment, activeSegmentIndex, direction, previousActiveSegment]);
  const handleChangeSegment = segment => {
    const {
      boundSegmentIndex,
      travelerIds
    } = segment;
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingSegment: 'SEGMENTS'
    }, {
      event: 'seatingVisible',
      seatingCurrentSegment: findIndexOfSegment(flattenedBounds, boundSegmentIndex) + 1
    });
    const deckOptions = getAvailableDeckOptionsForSegment(boundSegmentIndex);
    setActiveDeck(getActiveDeck(deckOptions));
    setActiveSegment(boundSegmentIndex);
    setActiveTraveler(getInitialActiveTravelerId(travelerIds));
    setIsSummaryShowing(false);
    setFromSummary(false);
  };
  const handleNextSegmentClick = () => {
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingSegment: 'CONTINUE'
    });
    if (isSummaryShowing) {
      return;
    }
    const isLastAvailableSegmentActive = isLastAvailableSegment(flattenedBounds, activeSegment);
    if (isLastAvailableSegmentActive) {
      if (usePersuasionConfiguration && isManualNavigationEnabled && !allSeatsSelected) {
        setConfirmationModalType(CONFIRMATION_MODAL_TYPE.SEAT_MAP_PERSIST_IN_PRODUCT_UNBOOKED_SEGMENTS);
        setConfirmationModalRedirectTo(CONFIRMATION_MODAL_REDIRECT_TO.SUMMARY);
        setShowAllSeatsSelectedConfirmation(true);
      } else {
        setIsSummaryShowing(true);
        setFromSummary(false);
      }
    } else {
      const segment = findNextAvailableSegment(flattenedBounds, activeSegment);
      if (segment) {
        handleChangeSegment(segment);
      }
    }
  };
  const getCurrentSegmentHeader = endpoint => {
    const segment = flattenedBounds.find(_ref3 => {
      let {
        boundSegmentIndex
      } = _ref3;
      return boundSegmentIndex === activeSegment;
    });
    return segment[endpoint].city;
  };
  const handleChangeSeat = _ref4 => {
    let {
      carrier,
      column,
      price,
      row,
      seat,
      markup,
      seatPreference
    } = _ref4;
    return () => {
      if (getSelected(activeSegment).some(s => s.value === seat) && !seatPreference) {
        if (isManualNavigationEnabled && selectedSeats) {
          setActiveTraveler(getSelected(activeSegment).find(s => s.value === seat).id);
        }
        return;
      }
      onChange(activeSegment, activeTraveler, carrier, column, row, seat, price, markup, seatPreference);
    };
  };
  const handleChangeTraveler = traveler => () => {
    setActiveTraveler(traveler);
    if (traveler !== 0) {
      setDirection(0);
    }
    if (fromSummary) {
      setFromSummary(false);
    }
  };
  const handleOnClose = data => {
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingSegment: 'BOOK'
    }, {
      event: 'seatingVisible',
      seatingOnBook: 'YES'
    });
    if (isFunction(onClose)) {
      onClose(data);
    }

    // TODO: AN-2261 - Add flow tests for force enabled seat map modal
    if (isFunction(callback)) {
      callback(selectedSeats);
    }
  };
  const handleDeckChange = option => {
    setActiveDeck(option);
  };
  const handleOnOptOut = async () => {
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingSegment: 'NO'
    });
    if (isFunction(onOptOut)) {
      await onOptOut();
    }
    if (isFunction(callback)) {
      callback();
    }
  };
  const handleRemoveSeat = (segment, traveler) => () => {
    onRemove(segment, traveler);
  };
  const handleToggleSeatSelect = (boundSegmentIndex, traveler) => () => {
    setActiveSegment(boundSegmentIndex);
    setActiveTraveler(traveler);
    const deckOptions = getAvailableDeckOptionsForSegment(boundSegmentIndex);
    setActiveDeck(getActiveDeck(deckOptions));
    setIsSummaryShowing(false);
    setFromSummary(true);
  };
  const setActiveNextTraveler = () => {
    if (flattenedBounds.length === 0) {
      return;
    }
    const nextTraveler = getNextTravelerId(getSegmentTravelers(travelers, flattenedBounds, activeSegment), activeTraveler);
    if (nextTraveler !== null) {
      handleChangeTraveler(nextTraveler)();
    }
  };
  const handlePreviousSegmentClick = () => {
    const isFirstAvailableSegmentActive = isFirstAvailableSegment(flattenedBounds, activeSegment);
    if (isFirstAvailableSegmentActive) {
      return;
    }
    if (isSummaryShowing) {
      setIsSummaryShowing(false);
      setFromSummary(false);
    } else {
      const segment = findPreviousAvailableSegment(flattenedBounds, activeSegment);
      if (segment) {
        handleChangeSegment(segment);
      }
    }
  };
  const setActivePreviousTraveler = () => {
    if (flattenedBounds.length === 0) {
      return;
    }
    const previousTraveler = getPreviousTravelerId(getSegmentTravelers(travelers, flattenedBounds, activeSegment), activeTraveler);
    if (previousTraveler !== null) {
      handleChangeTraveler(previousTraveler)();
    }
  };
  useEffect(() => {
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingCurrentSegment: activeSegment
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const prevSelectedSeatsInSegmentCount = usePrevious(getSelected(activeSegment).length);
  useDidUpdateEffect(() => {
    if (flattenedBounds.length !== 0) {
      const {
        travelerIds = [],
        __typename: segmentType
      } = flattenedBounds.find(segment => segment.boundSegmentIndex === activeSegment);
      const selectedSeatsInSegmentCount = getSelected(activeSegment).length;
      const travelersAllHaveSeat = selectedSeatsInSegmentCount === travelerIds.length;
      if ((selectedSeatsInSegmentCount !== prevSelectedSeatsInSegmentCount && travelersAllHaveSeat || segmentType === SEATING_PREFERENCE_OPTIONS.SELL_SEATING_PREFERENCE || segmentType === SEATING_PREFERENCE_OPTIONS.SELL_SEATING_BESIDE) && !isManualNavigationEnabled) {
        handleNextSegmentClick();
      }
      const nextUnselectedTraveler = getIdOfFirstUnselectedTraveler(getSegmentTravelers(travelers, flattenedBounds, activeSegment), getSelected(activeSegment), activeTraveler);
      if (nextUnselectedTraveler !== null) {
        handleChangeTraveler(nextUnselectedTraveler)();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeats]);
  analytics.useLogMouseFlowTagEffect('Seatmap 3.0');
  const handleConfirmationSelection = (action, segment) => {
    setShowAllSeatsSelectedConfirmation(false);
    if (action === CONFIRMATION_MODAL_ACTION_TYPE.SEAT_MAP_PERSIST_ACTION_DISMISS) {
      if (totalPrice === 0 && isPostBooking) {
        handleOnOptOut();
        return;
      }
      if (isCancelButtonDisabled) {
        if (confirmationModalRedirectTo === CONFIRMATION_MODAL_REDIRECT_TO.SUMMARY) {
          setIsSummaryShowing(true);
          setFromSummary(false);
        } else {
          handleOnOptOut();
        }
      } else {
        handleOnClose();
      }
    } else {
      if (!segment) {
        return;
      }
      const nonBookedTravelerSeats = getNonBookedTravelerSeats(flattenedBounds.find(seg => seg.boundSegmentIndex === segment), totalSelectedSeats);
      handleToggleSeatSelect(segment, nonBookedTravelerSeats[0])();
    }
  };
  const handleCloseButton = () => {
    if (!isCancelButtonDisabled) {
      handleOnClose();
      return;
    }
    setConfirmationModalType(CONFIRMATION_MODAL_TYPE.SEAT_MAP_PERSIST_IN_PRODUCT);
    if (usePersuasionConfiguration && shouldShowConfirmationModal && p('IbeClient.TravelerDetails.Modal') === 'SEATMAP') {
      setConfirmationModalRedirectTo(CONFIRMATION_MODAL_REDIRECT_TO.PAYMENT);
      setShowAllSeatsSelectedConfirmation(true);
    } else {
      handleOnOptOut();
    }
  };
  const showCloseButtonDependingOnDynamicFlowEnabled = p('TravelerDetails.DynamicFlow.Enabled') && isPostBooking || !p('TravelerDetails.DynamicFlow.Enabled');
  const isCloseButtonDisplayed = (isShowingCloseIcon || isCancelButtonDisabled) && showCloseButtonDependingOnDynamicFlowEnabled;
  return /*#__PURE__*/_jsxs("div", {
    className: seatMapWrapperStyles(backDrop),
    "data-offer-rate": SEAT_MAP.id,
    "data-testid": "seatMapContent-wrapper",
    children: [isCloseButtonDisplayed && /*#__PURE__*/_jsx(CloseIcon, {
      onClose: handleCloseButton
    }), /*#__PURE__*/_jsx(SeatMapHeader, {
      active: [activeSegment, activeTraveler],
      allSelectedSeats: selectedSeats,
      flattenedBounds: flattenedBounds,
      from: getCurrentSegmentHeader('from'),
      onNavigationClick: onNavigationClick,
      onNextSegmentClick: handleNextSegmentClick,
      onPreviousSegmentClick: handlePreviousSegmentClick,
      onSegmentClick: handleChangeSegment,
      shouldShowSummary: isSummaryShowing,
      to: getCurrentSegmentHeader('to')
    }), isSummaryShowing ? /*#__PURE__*/_jsx(Summary, {
      getSelected: getSelected,
      onRemoveSeat: handleRemoveSeat,
      onToggleSeatSelect: handleToggleSeatSelect,
      segments: flattenedBounds,
      totalMarkup: totalMarkup,
      totalPrice: totalPrice,
      travelers: travelers
    }) : /*#__PURE__*/_jsx(MainContent, {
      active: [activeSegment, activeTraveler],
      animate: animate,
      deckOptions: getAvailableDeckOptionsForSegment(activeSegment),
      direction: direction,
      fromSummary: fromSummary,
      handleBackDrop: handleBackDrop,
      onChangeSeat: handleChangeSeat,
      onChangeTraveler: handleChangeTraveler,
      onDeckChange: handleDeckChange,
      onNextTravelerClick: setActiveNextTraveler,
      onPreviousTravelerClick: setActivePreviousTraveler,
      onRemoveSeat: handleRemoveSeat,
      onSaveAndContinue: handleNextSegmentClick,
      productSpecificTexts: productSpecificTexts,
      segments: flattenedBounds,
      selectedDeck: activeDeck,
      selectedSeats: getSelected(activeSegment),
      travelers: getSegmentTravelers(travelers, flattenedBounds, activeSegment)
    }), /*#__PURE__*/_jsx("div", {
      className: footerWrapperStyles(showAllSeatsSelectedConfirmation),
      children: /*#__PURE__*/_jsx(Footer, {
        areAllSeatsSelected: isCancelButtonDisabled ? true : allSeatsSelected,
        isPostBooking: isPostBooking,
        onClose: handleOnClose,
        onOptOut: isCancelButtonDisabled ? null : handleOnOptOut,
        onSaveAndContinue: handleNextSegmentClick,
        selectNoOption: handleOnOptOut,
        setShowAllSeatsSelectedConfirmation: isCancelButtonDisabled ? null : setShowAllSeatsSelectedConfirmation,
        shouldShowSummary: isSummaryShowing,
        totalPrice: totalPrice
      })
    }), /*#__PURE__*/_jsx(AnimatePresence, {
      children: showAllSeatsSelectedConfirmation && /*#__PURE__*/_jsx(motion.div, {
        animate: "enter",
        exit: "exit",
        transition: {
          type: 'tween',
          duration: 0.3
        },
        variants: motionVariants,
        children: /*#__PURE__*/_jsx(SeatMapConfirmation, {
          moveToExtraProductsPage: moveToExtraProductsPage,
          onSelection: handleConfirmationSelection,
          redirectTo: confirmationModalRedirectTo,
          segments: nonSelectedAvailableSegments,
          type: confirmationModalType
        })
      }, "seatMapContent-allSeatsSelectedConfirmation")
    })]
  });
};
SeatMapContent.propTypes = {
  bounds: PropTypes.arrayOf(PropTypes.any),
  callback: PropTypes.func,
  getSelected: PropTypes.func,
  isPostBooking: PropTypes.bool,
  isShowingCloseIcon: PropTypes.bool,
  moveToExtraProductsPage: PropTypes.func,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  onOptOut: PropTypes.func,
  onRemove: PropTypes.func,
  productSpecificTexts: PropTypes.shape({
    seatMapSegmentTexts: PropTypes.arrayOf(PropTypes.shape({
      boundIndex: PropTypes.number,
      segmentIndex: PropTypes.number,
      text: PropTypes.string
    }))
  }),
  selectedDeck: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string
  }),
  selectedSeats: PropTypes.oneOfType([PropTypes.objectOf(PropTypes.any), PropTypes.arrayOf(PropTypes.any)]),
  shouldShowConfirmationModal: PropTypes.bool,
  totalMarkup: PropTypes.number,
  totalPrice: PropTypes.number,
  travelers: PropTypes.arrayOf(PropTypes.shape({
    ageType: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string
  }))
};
export default SeatMapContent;
//# sourceMappingURL=SeatMapContent.js.map